// src/pages/dashboard/branch/analytics/components/DateRangePicker.tsx
import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar, FiChevronDown } from "react-icons/fi";
import { motion } from "framer-motion";

interface DateRangePickerProps {
    startDate: Date | null;
    endDate: Date | null;
    onStartDateChange: (date: Date | null) => void;
    onEndDateChange: (date: Date | null) => void;
    maxDate?: Date;
    minDate?: Date;
    disabled?: boolean;
}

interface CustomInputProps {
    value?: string;
    onClick?: () => void;
    placeholder?: string;
    disabled?: boolean;
}

const CustomInput = forwardRef<HTMLButtonElement, CustomInputProps>(
    ({ value, onClick, placeholder, disabled }, ref) => (
        <button
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            className={`
                flex items-center gap-2 px-4 py-2.5 rounded-xl 
                bg-white dark:bg-gray-800 
                border border-gray-200 dark:border-gray-700 
                hover:border-primary/50 dark:hover:border-primary/50
                focus:outline-none focus:ring-2 focus:ring-primary/20 
                text-sm font-medium text-gray-700 dark:text-gray-200 
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                min-w-[140px]
            `}
        >
            <FiCalendar className="w-4 h-4 text-gray-400" />
            <span className={value ? "" : "text-gray-400"}>
                {value || placeholder}
            </span>
            <FiChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
        </button>
    )
);
CustomInput.displayName = "CustomInput";

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    maxDate = new Date(),
    minDate,
    disabled = false,
}) => {
    const { t } = useTranslation();

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap items-center justify-center gap-4 space-x-0 space-y-4 sm:space-x-4 sm:space-y-0"
        >
            <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("analytics.from")}
                </span>
                <DatePicker
                    selected={startDate}
                    onChange={onStartDateChange}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={endDate || maxDate}
                    minDate={minDate}
                    dateFormat="MMM d, yyyy"
                    customInput={<CustomInput placeholder={t("analytics.startDate")} />}
                    disabled={disabled}
                    popperClassName="date-picker-popper"
                    popperPlacement="bottom-start"
                    showPopperArrow={false}
                />
            </div>

            <div className="h-px w-4 bg-gray-300 dark:bg-gray-600" />

            <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("analytics.to")}
                </span>
                <DatePicker
                    selected={endDate}
                    onChange={onEndDateChange}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate || minDate}
                    maxDate={maxDate}
                    dateFormat="MMM d, yyyy"
                    customInput={<CustomInput placeholder={t("analytics.endDate")} />}
                    disabled={disabled}
                    popperClassName="date-picker-popper"
                    popperPlacement="bottom-start"
                    showPopperArrow={false}
                />
            </div>
        </motion.div>
    );
};

export default DateRangePicker;
